// -----------------------------------------------------------------------------
// Library
// -----------------------------------------------------------------------------
import React, { useEffect } from "react";
import { Button, Card, DatePicker, Flex, Space, Tag } from "antd";
import { useParams } from "react-router";
import { CaretDownFilled } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import moment from "moment";

// -----------------------------------------------------------------------------
// Utils and Styles
// -----------------------------------------------------------------------------
import StyledAnalytics from "./styles";
import AnalyticsTable from "./AnalyticsTable";

// -----------------------------------------------------------------------------
// Store
// -----------------------------------------------------------------------------
import {
	useGetCategoriesQuery,
	useGetDomainGymQuery,
	useGetGymWorkoutsQuery,
	useGetStatsForGymQuery,
} from "store/api/api";
import { updateMembersWorkoutsSelectedCategories } from "store/slices";
import { selectMembersWorkoutsSelectedCategories } from "store/selectors";
import Dropdown from "components/Dropdown";
import "./styles.scss";

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
function Analytics() {
	const dispatch = useDispatch();
	const { RangePicker } = DatePicker;
	let { gym } = useParams();
	const domainGymResponse = useGetDomainGymQuery(gym);
	const domainGymId = domainGymResponse?.data?.id;

	const dateRangeInitState = {
		startDate: "",
		endDate: "",
	}

	const [dateFilter, setDateFilter] = React.useState(dateRangeInitState);
	const categoriesFilters = useSelector(selectMembersWorkoutsSelectedCategories);
	const categoriesResponse = useGetCategoriesQuery(domainGymId, { skip: !domainGymResponse.data });
	const categories = categoriesResponse.data?.categories || [];
	const [homeCardData, setHomeCardData] = React.useState({
		videoViews: 0,
		activeUsers: 0,
		avgViewsPerUser: 0,
	});

	const { data: statsData, isFetching } = useGetStatsForGymQuery(
		{
			gym_id: domainGymId,
			start_date: dateFilter.startDate ? dateFilter.startDate.format('YYYY-MM-DD') : '',
			end_date: dateFilter.endDate ? dateFilter.endDate.format('YYYY-MM-DD') : '',
		},
		{
			skip: !domainGymResponse.data,
		}
	);

	const workoutsResponse = useGetGymWorkoutsQuery({ gym_id: domainGymId }, { skip: !domainGymResponse.data });
	const [workouts, setWorkouts] = React.useState([]);

	const handleCategoryClick = (category) => {
		const category_id = parseInt(category.key);

		if (!categoriesFilters.includes(category_id)) {
			if (category_id === 0) {
				dispatch(updateMembersWorkoutsSelectedCategories([0]));
			} else {
				if (categoriesFilters.includes(0)) {
					dispatch(updateMembersWorkoutsSelectedCategories([category_id]));
				} else {
					dispatch(updateMembersWorkoutsSelectedCategories([...categoriesFilters, category_id]));
				}
			}
		} else if (category_id !== 0) {
			const newCategories = [...categoriesFilters];

			newCategories.splice(
				categoriesFilters.findIndex((c) => c === category_id),
				1
			);

			dispatch(updateMembersWorkoutsSelectedCategories(newCategories.length > 0 ? newCategories : [0]));
		}
	};

	const categoryItems = categories.map((category) => {
		return {
			label: category.name,
			key: category.id,
			danger: categoriesFilters.includes(category.id) ? true : false,
			className: "analytics-menu-item",
			title: category.name,
		};
	});

	useEffect(() => {
		return () => {
			dispatch(updateMembersWorkoutsSelectedCategories([0]));
		};
	}, [dispatch]);

	const categoriesMenu = {
		items: [
			{
				label: "All Categories",
				key: 0,
				danger: categoriesFilters.includes(0) ? true : false,
			},
			...categoryItems,
		],
		onClick: handleCategoryClick,
	};

	React.useEffect(() => {
		if (statsData) {
			// Filter Workouts Data By Category
			const filteredWorkouts =
				workouts?.filter(
					(workout) =>
						categoriesFilters.includes(0) || workout.categories.some((c) => categoriesFilters.includes(c.category_id))
				) ?? [];

			// Filter Stats Data By Workouts
			const statsFiltered = statsData.filter((stat) =>
				filteredWorkouts.map((workout) => workout.key).includes(stat.target)
			);

			const totalViews = statsFiltered.reduce((acc, stat) => {
				return stat.total_views + acc;
			}, 0);

			const uniqueUserIds = new Set(
				statsFiltered.flatMap((stat) => (stat?.user_ids ? stat.user_ids.split(",")?.filter((id) => id) : []))
			);

			// Count the number of unique user IDs
			const uniqueUserCount = uniqueUserIds.size;

			// Calculate the total song plays
			const totalSongPlays =
				statsFiltered.reduce((acc, stat) => {
					return stat.total_song_plays + acc;
				}, 0) || 0;

			const toSet = {
				videoViews: totalViews,
				activeUsers: uniqueUserCount,
				avgViewsPerUser: ((totalViews || 0) / (uniqueUserCount || 1)).toFixed(2),
				totalSongPlays: totalSongPlays,
			};

			setHomeCardData(toSet);
		}
	}, [statsData, workouts, categoriesFilters]);

	const onDateRangeChange = (dates) => {
		if (dates) {
			setDateFilter({
				startDate: dates[0],
				endDate: dates[1],
			});
		}
	};

	React.useEffect(() => {
		if (workoutsResponse.data) {
			setWorkouts(workoutsResponse.data.workouts);
		}
	}, [workoutsResponse]);

	const resetDates = () => {
  		setDateFilter(dateRangeInitState)
	};

	const isVisibleDateChip = dateFilter.startDate && dateFilter.endDate;
	

	return (
		<StyledAnalytics className="sb-analytics">
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					paddingBottom: "16px",
					flexWrap: "wrap",
				}}
			>
				<h1>Analytics</h1>
				<div
					className="sb-analytics__filters"
					style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}
				>
					<Space.Compact size="large">
						<Dropdown
							trigger={["click"]}
							overlayClassName="sb-analytics_dropdown"
							style={{ flex: 1 }}
							menu={categoriesMenu}
						>
							<Button
								style={{
									borderColor: " #E3E5E7",
									textTransform: "none",
									color: "#4F5661",
									fontWeight: "700",
									fontSize: "14px",
									lineHeight: "24px",
									fontFamily: "DM Sans",
									width: "100%",
								}}
							>
								Categories({categoriesFilters?.length})
								<CaretDownFilled
									style={{
										color: "#4F5661",

										fontSize: "12px",
										lineHeight: "0px",
									}}
								/>
							</Button>
						</Dropdown>
					</Space.Compact>
					<Space.Compact size="large">
						<div className="sb-analytics__filters__range_picker">
							<RangePicker
								value={[dateFilter.startDate, dateFilter.endDate]}
								allowClear={false}
								popupStyle={{ zIndex: 99999 }}
								presets={[
									{
										label: "Today",
										value: [dayjs().startOf("day"), dayjs().endOf("day")],
									},
									{
										label: "This Week",
										value: [dayjs().startOf("week"), dayjs().endOf("week")],
									},
									{
										label: "Last Month",
										value: [
											dayjs().startOf("month").subtract(1, "months"),
											dayjs().startOf("month").subtract(1, "days"),
										],
									},
									{
										label: "This Month",
										value: [dayjs().startOf("month"), dayjs().endOf("month")],
									},
									{
										label: "Last Year",
										value: [dayjs().startOf("year").subtract(1, "years"), dayjs().startOf("year").subtract(1, "days")],
									},
									{
										label: "This Year",
										value: [dayjs().startOf("year"), dayjs().endOf("year")],
									},
								]}
								onChange={onDateRangeChange}
								placement="bottomRight"
								popupClassName="custom-picker-range-popup-responsive"
								getPopupContainer={(trigger) => trigger.parentNode}
								data-id="datePickerFilter"
							/>
						</div>
					</Space.Compact>
				</div>
			</div>
			{
				dateFilter.startDate && dateFilter.endDate && (
					<Tag
						closable={isVisibleDateChip}
						onClose={resetDates}
						bordered={false}
						style={{
							backgroundColor: `var(--background-color)`,
							borderRadius: 100,
							padding: '1px 8px'
						}}
					>
						<span
							style={{
								color: `var(--primary-color)`,
							}}>
							{moment(dateFilter.startDate.toDate()).format('YYYY-MM-DD')} - {moment(dateFilter.endDate.toDate()).format('YYYY- MM-DD')}
						</span>
					</Tag>
				)
			}
			<Flex className="sb-analytics__cards-container" justify="left" wrap>
				<Card hoverable className="sb-creator-home__section__card">
					<p className="sb-creator-home__section__card__title">VIDEO VIEWS</p>
					<p className="sb-creator-home__section__card__value">{homeCardData["videoViews"]}</p>
				</Card>
				<Card hoverable className="sb-creator-home__section__card">
					<p className="sb-creator-home__section__card__title">TOTAL ACTIVE USERS</p>
					<p className="sb-creator-home__section__card__value">{homeCardData["activeUsers"]}</p>
				</Card>
				<Card hoverable className="sb-creator-home__section__card">
					<p className="sb-creator-home__section__card__title">AVG. VIEWS PER USER</p>
					<p className="sb-creator-home__section__card__value">{homeCardData["avgViewsPerUser"]}</p>
				</Card>
				<Card hoverable className="sb-creator-home__section__card">
					<p className="sb-creator-home__section__card__title">SONG PLAYS</p>
					<p className="sb-creator-home__section__card__value">{homeCardData["totalSongPlays"]}</p>
				</Card>
			</Flex>
			<div style={{ display: "flex", justifyContent: "left", paddingTop: "16px" }}>
				<h1>Most Popular Workouts</h1>
			</div>
			<AnalyticsTable loading={isFetching} statsData={statsData} workoutsResponse={workoutsResponse} />
		</StyledAnalytics>
	);
}

export default Analytics;
